import clsx from "clsx";

import { formatBytes } from "../lib/format";
import { useUploads, type UploadItem } from "../lib/uploadQueue";
import { describePhase } from "./DevelopingCard";

/**
 * The queue of photographs still going up.
 *
 * It lives in the app shell, not in a route, because the state behind it lives
 * at the root: you can start six uploads, wander into a collection, and they
 * carry on regardless.
 */
export function UploadTray() {
  const { items, cancel, retry, dismiss } = useUploads();

  if (items.length === 0) return null;

  const inFlight = items.filter((item) => item.status !== "error");

  return (
    <section
      aria-label="Photographs being sent"
      className="pointer-events-auto fixed inset-x-0 bottom-0 z-30 px-3 pb-[calc(5.25rem+env(safe-area-inset-bottom))] sm:left-auto sm:right-4 sm:w-80 sm:pb-4"
    >
      <div className="sheet grain animate-rise relative overflow-hidden shadow-lifted">
        <header className="flex items-baseline justify-between gap-2 border-b border-rule bg-paper-deep px-3 py-2">
          <p className="field-label text-ink">
            {inFlight.length > 0
              ? `Sending ${inFlight.length} ${inFlight.length === 1 ? "photograph" : "photographs"}`
              : "Not sent"}
          </p>
          <p role="status" aria-live="polite" className="sr-only">
            {inFlight.length > 0 ? `${inFlight.length} photographs uploading` : "Uploads finished"}
          </p>
        </header>

        <ul className="max-h-64 divide-y divide-rule overflow-y-auto">
          {items.map((item) => (
            <TrayRow
              key={item.localId}
              item={item}
              onCancel={() => cancel(item.localId)}
              onRetry={() => retry(item.localId)}
              onDismiss={() => dismiss(item.localId)}
            />
          ))}
        </ul>
      </div>
    </section>
  );
}

function TrayRow({
  item,
  onCancel,
  onRetry,
  onDismiss,
}: {
  item: UploadItem;
  onCancel: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const failed = item.status === "error";
  const percent = Math.round(item.progress * 100);

  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <span className="relative block size-10 shrink-0 overflow-hidden bg-paper-deep">
        {item.previewUrl ? (
          <img
            src={item.previewUrl}
            alt=""
            className={clsx(
              "h-full w-full object-cover",
              failed ? "opacity-40 saturate-0" : "opacity-70 saturate-50",
            )}
          />
        ) : null}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate font-prose text-sm text-ink">{item.name}</p>
        <p className={clsx("mt-0.5 field-label-tight", failed ? "text-rust" : "text-ink-soft")}>
          {failed ? (item.error ?? "Didn't send") : describePhase(item.status).line}
        </p>

        {!failed ? (
          <div className="mt-1.5 h-1 w-full bg-paper-deep">
            <div
              className="h-full bg-ochre transition-[width] duration-300 ease-out"
              style={{
                width: `${item.status === "uploading" ? percent : item.status === "waiting" ? 2 : 100}%`,
              }}
            />
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1">
        {failed ? (
          <>
            {item.retryable ? (
              <button type="button" onClick={onRetry} className="link font-label text-xs">
                Retry
              </button>
            ) : null}
            <button
              type="button"
              onClick={onDismiss}
              className="font-label text-xs text-ink-soft underline decoration-rule underline-offset-2 hover:text-ink"
            >
              Dismiss
            </button>
          </>
        ) : (
          <>
            <span className="font-label text-xs tabular-nums text-ink-soft">
              {item.status === "uploading" ? `${percent}%` : formatBytes(item.size)}
            </span>
            <button
              type="button"
              onClick={onCancel}
              aria-label={`Cancel upload of ${item.name}`}
              className="font-label text-xs text-ink-soft underline decoration-rule underline-offset-2 hover:text-ink"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </li>
  );
}
